import * as d3 from 'd3';

/**
 * The "this is the node you started from" highlight, used while a Link to Existing…
 * selection is in flight.
 *
 * There were two defects here, both visible in browser QA:
 *
 * 1. **The highlight was too thin to read.** It was applied as
 *    `style('stroke-width', '.25em')`, but the canvas draws a node symbol with
 *    `attr('stroke-width', 10)` in CHART units on a zoomed surface — `.25em` resolves
 *    against the path's font size and lands far below that, so the "highlighted" node
 *    came out THINNER than its neighbours instead of standing out.
 * 2. **Cancelling left it behind.** `cancelSelectionMode` restored the canvas fill and
 *    nothing else, so pressing Esc or Cancel left the node permanently mis-stroked.
 *    The completion path did restore it, but from a selection captured at render time
 *    which goes stale whenever the SVG is rebuilt.
 *
 * So: one module, selections taken FRESH at call time, and the previous inline stroke
 * stashed on the element rather than restored from a hardcoded value — the canvas is
 * free to change how it strokes a symbol without silently breaking the restore.
 */

const PREV_STROKE = '__tgPrevStroke';

/** Chart units, matching the canvas's own `attr('stroke-width', 10)` scale. */
export const SELECTION_STROKE_WIDTH = 28;
export const SELECTION_STROKE = '#1976d2';

const symbolsIn = (root) => d3.select(root || document).selectAll('path.te');

/** The datum may be the person, or a laid-out wrapper around it. */
const pidOf = (d) => d?.pid ?? d?.data?.pid;

/**
 * Put every symbol back. The width style is REMOVED rather than reset, so the
 * canvas's own `stroke-width` attribute takes over again; the colour is restored from
 * what was stashed on entry.
 */
export const clearSelectionHighlight = (root) => {
  symbolsIn(root).each(function () {
    if (!(PREV_STROKE in this)) return;
    const prev = this[PREV_STROKE];
    delete this[PREV_STROKE];
    d3.select(this)
      .style('stroke', prev || null)
      .style('stroke-width', null);
  });
};

/**
 * Stroke the symbol for `pid` as the selection target, clearing any previous one.
 * Safe to call repeatedly.
 */
export const highlightSelectedNode = (root, pid) => {
  clearSelectionHighlight(root);
  if (pid == null) return;

  symbolsIn(root)
    .filter((d) => pidOf(d) === pid)
    .each(function () {
      // Stash whatever the canvas had inline so the restore cannot drift from it.
      this[PREV_STROKE] = this.style.stroke || '';
    })
    .style('stroke', SELECTION_STROKE)
    .style('stroke-width', SELECTION_STROKE_WIDTH);
};
